import { W, H, plateSeeds, elementState, tickerX, type Box } from './geometry';
import type { Recipe, Element, Plate } from './recipe';

// The canvas preview's drawing: the plate approximated with 2D-canvas
// primitives from the SAME seeds the render plane uses (plateSeeds), and the
// elements placed by the SAME state function (elementState), so what moves
// here moves there. Fidelity is the render plane's; timing and placement are
// shared. Pure where it can be: the helpers below are tested; the painters
// take a context.

const TAU = Math.PI * 2;

function hexRgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  return m ? [parseInt(m[1]!, 16), parseInt(m[2]!, 16), parseInt(m[3]!, 16)] : [16, 20, 24];
}
export function rgba(hex: string, a: number): string { const [r, g, b] = hexRgb(hex); return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a))})`; }
export function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hexRgb(a), [r2, g2, b2] = hexRgb(b);
  const k = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(r1 + (r2 - r1) * k)},${Math.round(g1 + (g2 - g1) * k)},${Math.round(b1 + (b2 - b1) * k)})`;
}

/** Phase of the loop at t, 0..1. */
export function phase(t: number, seconds: number): number { const p = (t / seconds) % 1; return p < 0 ? p + 1 : p; }

/** Paint a plate at time t on a context scaled so that 1 unit = 1 canvas px of 1920×1080. */
export function drawPlate(ctx: CanvasRenderingContext2D, plate: Plate | null, t: number, seconds: number): void {
  ctx.fillStyle = plate?.ground ?? '#101418';
  ctx.fillRect(0, 0, W, H);
  if (!plate || plate.kind === 'none' || plate.kind === 'still') return;
  const p = phase(t, seconds) * plate.speed;
  const seeds = plateSeeds(plate.kind, plate.seed, plate.density);
  const accent = plate.accent, second = plate.second ?? mix(plate.accent, '#ffffff', 0.35);
  if (plate.kind === 'streaks') {
    for (const s of seeds.streaks) {
      const travel = W + s.len;
      const x = ((s.x0 + s.n * travel * p) % travel) - s.len;
      const g = ctx.createLinearGradient(x, 0, x + s.len, 0);
      g.addColorStop(0, rgba(accent, 0)); g.addColorStop(0.6, rgba(accent, s.a)); g.addColorStop(1, rgba(second, 0));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.moveTo(x, s.y); ctx.lineTo(x + s.len, s.y - 60); ctx.lineTo(x + s.len + 40, s.y - 40); ctx.lineTo(x + 40, s.y + 20); ctx.closePath(); ctx.fill();
    }
  } else if (plate.kind === 'worldmap') {
    for (const d of seeds.dots) {
      const b = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(TAU * (d.k * p + d.p)));
      ctx.fillStyle = rgba(accent, 0.25 + 0.55 * b);
      ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, TAU); ctx.fill();
    }
    ctx.strokeStyle = rgba(second, 0.18); ctx.lineWidth = 1.5;
    for (let i = 0; i + 1 < seeds.dots.length; i += 6) {
      const a = seeds.dots[i]!, b = seeds.dots[i + 1]!;
      const k = ((a.k * p + a.p) % 1);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k); ctx.stroke();
    }
  } else if (plate.kind === 'starburst') {
    const cx = W / 2, cy = H * 0.55, rays = Math.round(36 * plate.density);
    const rot = TAU * p * Math.max(1, Math.round(plate.speed));
    for (let i = 0; i < rays; i++) {
      const a0 = rot + (i / rays) * TAU, a1 = a0 + (TAU / rays) * 0.45;
      const pulse = 0.5 + 0.5 * Math.sin(TAU * (2 * p) + i);
      ctx.fillStyle = rgba(i % 2 ? accent : second, 0.05 + 0.1 * pulse);
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(a0) * 2400, cy + Math.sin(a0) * 2400); ctx.lineTo(cx + Math.cos(a1) * 2400, cy + Math.sin(a1) * 2400); ctx.closePath(); ctx.fill();
    }
  } else if (plate.kind === 'bokeh') {
    for (const d of seeds.dots.slice(0, Math.round(40 * plate.density))) {
      const x = d.x + 80 * Math.sin(TAU * (d.k * p + d.p)), y = d.y + 50 * Math.cos(TAU * (d.k * p + d.p));
      const r = 40 + d.r * 30, a = 0.08 + 0.12 * (0.5 + 0.5 * Math.sin(TAU * (d.k * p * 2 + d.p)));
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, rgba(d.k === 2 ? second : accent, a)); g.addColorStop(1, rgba(accent, 0));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, TAU); ctx.fill();
    }
  } else if (plate.kind === 'aurora') {
    for (let j = 0; j < 4; j++) {
      const y0 = H * (0.35 + 0.15 * j);
      ctx.beginPath(); ctx.moveTo(0, H);
      for (let x = 0; x <= W; x += 40) {
        const y = y0 + 60 * Math.sin(TAU * (x / 900 + (j + 1) * p)) + 30 * Math.sin(TAU * (x / 400 - (j + 2) * p));
        ctx.lineTo(x, y);
      }
      ctx.lineTo(W, H); ctx.closePath();
      const g = ctx.createLinearGradient(0, y0 - 100, 0, H);
      g.addColorStop(0, rgba(j % 2 ? second : accent, 0.22)); g.addColorStop(1, rgba(plate.ground, 0));
      ctx.fillStyle = g; ctx.fill();
    }
  } else if (plate.kind === 'grid') {
    const step = 96;
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += step) { ctx.strokeStyle = rgba(accent, 0.12 + 0.12 * (0.5 + 0.5 * Math.sin(TAU * (x / W * 3 - 2 * p)))); ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += step) { ctx.strokeStyle = rgba(accent, 0.12 + 0.12 * (0.5 + 0.5 * Math.sin(TAU * (y / H * 2 + 2 * p)))); ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    for (const d of seeds.dots.slice(0, Math.round(60 * plate.density))) {
      const gx = Math.round(d.x / step) * step, gy = Math.round(d.y / step) * step;
      ctx.fillStyle = rgba(second, 0.2 + 0.6 * (0.5 + 0.5 * Math.sin(TAU * (d.k * p + d.p))));
      ctx.beginPath(); ctx.arc(gx, gy, 3, 0, TAU); ctx.fill();
    }
  }
}

export interface Placed { el: Element; box: Box; alpha: number; reveal: number; message: number; visible: boolean }

/** Every element's box and state at t — the one function the canvas and the stage both call. */
export function layout(r: Recipe, t: number): Placed[] {
  return r.elements.map((el) => {
    const n = el.type === 'band' ? Math.max(1, el.content.messages?.length ?? 1) : 1;
    const st = elementState(t, r.seconds, n, el.motion.enter, el.motion.exit, el.box);
    return { el, box: { x: el.box.x + st.dx, y: el.box.y + st.dy, w: el.box.w, h: el.box.h }, alpha: st.alpha, reveal: st.reveal, message: st.message, visible: st.visible };
  });
}

/** Where a ticker strip of `stripWidth` px sits at t. */
export function tickerLeft(r: Recipe, stripWidth: number, t: number, speed = 110): number { return tickerX(t, r.seconds, stripWidth, speed); }

/** The band's message as words: a title line and a second line. */
export function messageLines(el: Element, index: number): { line: string; sub: string; glyphs: string[] } {
  const m = el.content.messages?.[index];
  const GLYPH: Record<string, string> = { youtube: '▶', instagram: '◎', facebook: 'f', tiktok: '♪', x: '𝕏', threads: '@', website: '⌂' };
  if (!m) return { line: el.content.text ?? '', sub: el.content.sub ?? '', glyphs: [] };
  if (m.kind === 'follow') return { line: m.text || 'FOLLOW US', sub: m.handle ?? '', glyphs: (m.platforms ?? []).map((p) => GLYPH[p] ?? '·') };
  if (m.kind === 'hashtag') return { line: m.tag ?? '', sub: m.sub ?? '', glyphs: [] };
  return { line: m.text ?? '', sub: m.sub ?? '', glyphs: [] };
}
